// components/SEO.jsx
// Per-route title/meta description, via react-helmet-async. Before this, every route shared the one
// static <title>/description from public/index.html, so search engines saw /game, /withdraw,
// /referral, /key-generator, and the homepage as near-duplicate pages. Each route now renders its
// own <SEO> with a distinct, keyword-relevant title/description; index.html's tags remain as the
// fallback for anything that doesn't (yet) render one.
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Jankenwin';
const SITE_URL = 'https://jankenwin.com';

// index.html and frontend/scripts/prerender.js both bake a static <title>/description/canonical/
// og:*/twitter:* set into the raw HTML (marked data-prerendered="true") so crawlers that don't
// execute JS still see correct per-route tags. react-helmet-async has no knowledge of those tags -
// it manages document.head directly and only ever adds its own, so once this component mounts and
// Helmet inserts the real tags, the static ones are left behind, producing duplicate <title>/meta
// tags in the JS-rendered DOM. Strip them once, here, since this is the component that's about to
// supersede them.
const removePrerenderedTags = () => {
  document.querySelectorAll('[data-prerendered="true"]').forEach((el) => el.remove());
};

const SEO = ({ title, description, path = '', noindex = false }) => {
  useEffect(() => { removePrerenderedTags(); }, []);

  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={`${SITE_URL}${path}`} />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={`${SITE_URL}${path}`} />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      {noindex && <meta name="robots" content="noindex, follow" />}
    </Helmet>
  );
};

export default SEO;
export { SITE_NAME, SITE_URL };
